import { Button, Drawer, Empty, Input, Segmented, Spin } from "antd";
import { useDebounceFn } from "ahooks";
import { useMemo, useState } from "react";

import styles from "./index.less";

import {
  FlowModuleTemplateType,
  isChannelNode,
  isComposeNode,
  ModuleTemplateType,
  ModuleTemplateTypeEnum,
} from "@packages/flow-core/module";
import { XYPosition, useViewport } from "reactflow";
import { moduleTemplatesList } from "../../../constants/modules/templates";
import { appModule2FlowNode } from "../../utils/adapt";
import { nanoid } from "nanoid";
import { useFlow } from "../FlowEdit/useFlow";
import { FlowNodeTypeEnum } from "@packages/flow-core/module/node/constants";

import { ReactComponent as SearchIcon } from "../../../assets/search.svg";
import { ReactComponent as NoPluginImage } from "../../../assets/no-plugin.svg";
import { usePageConfig } from "../../PageConfigProvider";
import { ReactComponent as MenuUnfoldIcon } from "../../../assets/menu-unfold-icon.svg";

export type MenuTemplateListDrawerProps = {
  visible: boolean;
  onClose: () => void;
  containerGetBoundingClientRect: () => DOMRect | undefined;
};

export default function MenuTemplateListDrawer(
  props: MenuTemplateListDrawerProps
) {
  const { visible, onClose, containerGetBoundingClientRect } = props;

  const { isInConsumerEnv } = usePageConfig();

  const { mode, templates, useablePlugins } = useFlow();
  // 基础节点、复合节点、特殊渠道节点
  const [nodeType, setNodeType] = useState<"basic" | "compose" | "channel">(
    "basic"
  );
  const [realtimeRenderTemplates, setRealtimeRenderTemplates] = useState<
    ModuleTemplateType[]
  >([]);
  const [searchNodeValue, setSearchNodeValue] = useState<string>("");

  const filterModules: Record<string, any> = useMemo(() => {
    if (mode === "plugin") {
      // 子流程不存在全局变量、用户输入、app调用节点
      return {
        [FlowNodeTypeEnum.globalVariable]: 1,
        [FlowNodeTypeEnum.userChatInput]: 1,
        [FlowNodeTypeEnum.invokeApp]: 1,
        [FlowNodeTypeEnum.templateParam]: 1,
      };
    }

    if (mode === "template") {
      // 模板不存在 app 调用节点
      return {
        [FlowNodeTypeEnum.templateParam]: 1,
      };
    }

    if (mode === "app") {
      // app 不存在插件输入、插件输出节点
      return {
        [FlowNodeTypeEnum.pluginInput]: 1,
        [FlowNodeTypeEnum.pluginOutput]: 1,
        [FlowNodeTypeEnum.templateParam]: 1,
      };
    }
    return {};
  }, [mode]);

  const formatedTemplates = useMemo(() => {
    if (!templates) return [];
    let templatesCopied: FlowModuleTemplateType[] = templates;

    const copy: ModuleTemplateType[] = JSON.parse(
      JSON.stringify(moduleTemplatesList)
    );

    if (!!useablePlugins && useablePlugins.length > 0) {
      templatesCopied = [...templates, ...useablePlugins];
    }

    if (nodeType === "basic") {
      templatesCopied
        .filter((mod) => !filterModules[mod.flowType])
        .filter(
          (mod) =>
            !isComposeNode(mod.templateType as ModuleTemplateTypeEnum) &&
            !isChannelNode(mod.templateType as ModuleTemplateTypeEnum)
        )
        .forEach((item) => {
          const index = copy.findIndex(
            (template) => template.type === item.templateType
          );
          if (index === -1) return;
          copy[index].list.push(item);
        });
    }

    if (nodeType === "compose") {
      // 复合节点
      templatesCopied
        .filter((mod) => !filterModules[mod.flowType])
        .filter((mod) =>
          isComposeNode(mod.templateType as ModuleTemplateTypeEnum)
        )
        .forEach((item) => {
          const index = copy.findIndex(
            (template) => template.type === item.templateType
          );
          if (index === -1) return;
          copy[index].list.push(item);
        });
    }

    if (nodeType === "channel") {
      // 特殊渠道节点
      templatesCopied
        .filter((mod) => !filterModules[mod.flowType])
        .filter((mod) =>
          isChannelNode(mod.templateType as ModuleTemplateTypeEnum)
        )
        .forEach((item) => {
          const index = copy.findIndex(
            (template) => template.type === item.templateType
          );
          if (index === -1) return;
          copy[index].list.push(item);
        });
    }

    const result = copy.filter(
      (item) =>
        // 下面逻辑是因为插件是前端动态渲染的
        (nodeType === "compose" &&
          item.type === ModuleTemplateTypeEnum.pluginCall) ||
        item.list.length > 0
    );

    setRealtimeRenderTemplates(result);
    return result;
  }, [templates, mode, nodeType, useablePlugins]);

  /**
   * 防抖搜索模板
   */
  const searchTemplatesFn = useDebounceFn(
    (search: string) => {
      let filtered: ModuleTemplateType[] = [];

      if (!search) {
        filtered = formatedTemplates;
      } else {
        formatedTemplates.forEach((t) => {
          const list = t.list.filter(
            ({ name, description }) =>
              name.includes(search) || description?.includes(search)
          );

          if (list.length > 0) {
            filtered.push({
              ...t,
              list,
            });
          }
        });
      }

      setRealtimeRenderTemplates(filtered);
    },
    {
      wait: 200,
    }
  );

  const viewport = useViewport();
  const { addNodes } = useFlow();

  function handleAddNode({
    template,
    position,
  }: {
    template: FlowModuleTemplateType;
    position: XYPosition;
  }) {
    const reactFlowBounds = containerGetBoundingClientRect();
    if (!reactFlowBounds) return;
    if (position.x < 300) return;
    const { x, y, zoom } = viewport;
    const mouseX = (position.x - reactFlowBounds.left - x) / zoom - 100;
    const mouseY = (position.y - reactFlowBounds.top - y) / zoom;

    addNodes(
      appModule2FlowNode({
        item: {
          ...template,
          moduleId: nanoid(),
          position: { x: mouseX, y: mouseY - 20 },
        },
      })
    );
  }

  const renderFormatedTemplates = useMemo(() => {
    if (!realtimeRenderTemplates?.length) {
      return (
        <Empty
          style={{ marginTop: 50 }}
          description={
            searchNodeValue.length > 0 ? "未搜索到模块" : "暂无可用模块"
          }
        />
      );
    }

    return realtimeRenderTemplates.map((template) => {
      return (
        <div key={template.type} className={styles.template}>
          <div className={styles.title}>{template.label}</div>
          {/* 特殊处理子流程模块 */}
          {template.type === ModuleTemplateTypeEnum.pluginCall &&
            template.list.length === 0 && (
              <Empty description="无子流程" image={<NoPluginImage />}>
                {/* C 端环境，不允许跳转 */}
                {!isInConsumerEnv && (
                  <div>
                    请先在
                    <a href="/operation/plugins">「子流程管理」</a>
                    新建
                  </div>
                )}
              </Empty>
            )}
          {template.list.map((item) => {
            return (
              <div
                key={item.id + item.flowType}
                className={styles.card}
                draggable
                onDragEnd={(e) =>
                  handleAddNode({
                    template: item,
                    position: { x: e.clientX, y: e.clientY },
                  })
                }
              >
                <img src={item.avatar} className={styles.avatar} />
                <div className={styles["card-content"]}>
                  <div className={styles.name}>{item.name}</div>
                  <div className={styles.desc}>{item.description}</div>
                </div>
              </div>
            );
          })}
        </div>
      );
    });
  }, [realtimeRenderTemplates, viewport]);

  return (
    <Drawer
      open={visible}
      mask={false}
      closable={false}
      rootClassName={styles.drawer}
      width={384}
      placement="left"
    >
      <Spin spinning={!templates?.length}>
        <div className={styles["template-type-header"]}>
          <div className={styles["template-type-header-line-1"]}>
            <div className={styles.search}>
              <Input
                className={styles["search-input"]}
                placeholder="搜索节点"
                value={searchNodeValue}
                allowClear={false}
                onChange={(e) => {
                  setSearchNodeValue(e.target.value.trim());
                  searchTemplatesFn.run(e.target.value.trim());
                }}
              />
              <SearchIcon className={styles["search-icon"]} />
            </div>
            <Button type="text" icon={<MenuUnfoldIcon />} onClick={onClose} />
          </div>
          <Segmented
            options={["基础节点", "复合节点", "特殊渠道节点"]}
            block
            style={{ width: "100%" }}
            onChange={(v) => {
              setSearchNodeValue("");

              if (v === "基础节点") {
                setNodeType("basic");
                return;
              }

              if (v === "复合节点") {
                setNodeType("compose");
                return;
              }

              if (v === "特殊渠道节点") {
                setNodeType("channel");
                return;
              }
            }}
          />
        </div>
        <div className={styles.content}>
          <div className={styles["template-list"]}>
            {renderFormatedTemplates}
          </div>
        </div>
      </Spin>
    </Drawer>
  );
}
